import React, { useMemo, useState } from 'react';
import { Icon } from '@tt/ui';
import cx from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import FilterGeneric from '../../../../components/FilterGeneric';
import { docTypeLabels as reportTypeLabels } from '../../../../components/RentersDashboard/RenterToDoCards/ConditionReportTodoCard/ConditionReportTodoCard';
import SearchBox from '../../../../components/SearchBox';
import CONDITION_REPORT_STATUS from '../../../../constants/condition_reports/status';
import { useLeases } from '../../../../core/TTgraphql';
import { segmentTracking } from '../../../../services/utilities';
import ConditionReportingEmptyState from '../../leases/view/Tabs/ConditionReporting/ConditionReportingEmptyState/ConditionReportingEmptyState';
import ConditionReportStatusTag from '../ConditionReportStatusTag';
import ReportAction from '../ReportAction';

import styles from './ConditionReportsTableDesktop.module.scss';

const statusOptions = [
  {
    label: 'Not Started',
    value: CONDITION_REPORT_STATUS.NOT_STARTED,
  },
  {
    label: 'In Progress',
    value: CONDITION_REPORT_STATUS.IN_PROGRESS,
  },
  {
    label: 'Needs Approval',
    value: CONDITION_REPORT_STATUS.NEEDS_APPROVAL,
  },
  {
    label: 'Complete',
    value: CONDITION_REPORT_STATUS.COMPLETE,
  },
  {
    label: 'Uploaded',
    value: CONDITION_REPORT_STATUS.UPLOADED,
  },
];

const typeOptions = [
  {
    label: reportTypeLabels.CONDITION_REPORT_MOVE_IN,
    value: 'CONDITION_REPORT_MOVE_IN',
  },
  {
    label: reportTypeLabels.CONDITION_REPORT_MOVE_OUT,
    value: 'CONDITION_REPORT_MOVE_OUT',
  },
  {
    label: reportTypeLabels.CONDITION_REPORT_MOVE_PERIODIC,
    value: 'CONDITION_REPORT_MOVE_PERIODIC',
  },
  {
    label: reportTypeLabels.OTHER,
    value: 'CONDITION_REPORT_OTHER',
  },
];

const ConditionReportsTableDesktop = ({
  className,
  conditionReports,
  goToLeasePage,
  onRowClick,
  createNewReportHandler,
  hideTitle,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'due_date',
    direction: 'desc',
  });

  const { leases, loading: loadingLeases } = useLeases({}, false);

  const [statusFilter, setStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [leaseFilter, setLeaseFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
  };

  const leasesFilterOptions = (leases || []).reduce((acc, lease) => {
    const hasReport = conditionReports.some(
      (report) => report.lease_id === lease.id,
    );
    if (hasReport) {
      acc.push({
        label: lease.title,
        value: lease.id,
      });
    }
    return acc;
  }, []);

  const typeFilterOptions = typeOptions.reduce((acc, type) => {
    const hasReport = conditionReports.some(
      (report) => report.type === type.value,
    );
    if (hasReport) {
      acc.push(type);
    }
    return acc;
  }, []);

  const statusFilterOptions = statusOptions.reduce((acc, status) => {
    const hasReport = conditionReports.some(
      (report) => report.status === status.value,
    );
    if (hasReport) {
      acc.push(status);
    }
    return acc;
  }, []);

  const handleStatusFilterChange = (selectedStatuses) => {
    setStatusFilter(selectedStatuses);
  };

  const handleTypeFilterChange = (selectedTypes) => {
    setTypeFilter(selectedTypes);
  };

  const handleLeaseFilterChange = (selectedLeases) => {
    setLeaseFilter(selectedLeases);
  };

  const filteredReports = useMemo(() => {
    if (!conditionReports) return [];
    let filtered = [...conditionReports];

    // Status filter
    if (statusFilter.length > 0 && !statusFilter.includes('all')) {
      filtered = filtered.filter((report) =>
        statusFilter.includes(report.status),
      );
    }

    // Type filter
    if (typeFilter.length > 0 && !typeFilter.includes('all')) {
      filtered = filtered.filter((report) => typeFilter.includes(report.type));
    }

    // Lease filter
    if (leaseFilter.length > 0 && !leaseFilter.includes('all')) {
      filtered = filtered.filter((report) =>
        leaseFilter.includes(report.lease_id),
      );
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter((report) =>
        (report.name || '').toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [conditionReports, statusFilter, typeFilter, searchQuery, leaseFilter]);

  const sortedReports = useMemo(() => {
    if (!filteredReports) return [];

    return [...filteredReports].sort((a, b) => {
      const dateA = a.due_date ? new Date(a.due_date) : new Date(0);
      const dateB = b.due_date ? new Date(b.due_date) : new Date(0);

      if (sortConfig.key === 'due_date') {
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      // Title sorting
      const titleA = a.name || '';
      const titleB = b.name || '';
      return sortConfig.direction === 'asc'
        ? titleA.localeCompare(titleB)
        : titleB.localeCompare(titleA);
    });
  }, [filteredReports, sortConfig]);

  const handleSort = (key) => {
    segmentTracking(key === 'due_date' ? 'due_date clicked' : 'title clicked', {
      location: 'Condition Reports Index Page',
    });
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return {
          key,
          direction: key === 'due_date' ? 'desc' : 'asc',
        };
      }
      return {
        key,
        direction: prev.direction === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'arrow-down';
    return sortConfig.direction === 'asc' ? 'arrow-up' : 'arrow-down';
  };

  return (
    <div className={cx(styles.container, className)}>
      {!hideTitle && sortedReports?.length ? (
        <div className={styles.header}>
          {!hideTitle && <div className={styles.title}>Condition Reports</div>}
          {sortedReports?.length && !hideTitle ? (
            <Button
              className={styles.createNewReport}
              onClick={() => createNewReportHandler(sortedReports[0]?.lease_id)}
            >
              Create New Report
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className={styles.filtersContainer}>
        <FilterGeneric
          filterName="Type"
          icon={() => (
            <Icon
              name="filter"
              className={cx(styles.icon, {
                [styles.iconSelected]: typeFilter.length,
              })}
            />
          )}
          className={styles.filter}
          options={typeFilterOptions}
          onChange={handleTypeFilterChange}
          hideSearch={true}
          staticFilterName="Type"
          selected={typeFilter}
        />
        <FilterGeneric
          filterName="Lease"
          loading={loadingLeases}
          options={leasesFilterOptions}
          className={styles.filter}
          onChange={handleLeaseFilterChange}
          selected={leaseFilter}
          hideSearch={true}
          staticFilterName="Lease"
          icon={() => (
            <Icon
              name="leases"
              className={cx(styles.icon, {
                [styles.iconSelected]: leaseFilter.length,
              })}
            />
          )}
          iconProps={{ color: 'primary' }}
        />
        <FilterGeneric
          filterName="Status"
          selected={statusFilter}
          onChange={handleStatusFilterChange}
          options={statusFilterOptions}
          icon={() => (
            <Icon
              name="filter"
              className={cx(styles.icon, {
                [styles.iconSelected]: statusFilter.length,
              })}
            />
          )}
          hideSearch={true}
          className={styles.filter}
          staticFilterName="Status"
        />
        <SearchBox
          placeholder="Search"
          onSearch={handleSearchQueryChange}
          value={searchQuery}
          className={styles.searchBox}
          debounceTime={500}
        />
        {sortedReports?.length && hideTitle ? (
          <Button
            className={styles.createNewReportButton}
            onClick={() => createNewReportHandler(sortedReports[0]?.lease_id)}
          >
            Create New Report
          </Button>
        ) : null}
      </div>
      {!sortedReports?.length ? (
        <Card className={styles.getStartedSection}>
          <ConditionReportingEmptyState
            onClick={() => createNewReportHandler(sortedReports[0]?.lease_id)}
          />
        </Card>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div
              className={cx(styles.columnDueDate, styles.sortableHeader)}
              onClick={() => handleSort('due_date')}
            >
              <span
                className={cx(styles.headerText, {
                  [styles.selected]: sortConfig.key === 'due_date',
                })}
              >
                DUE DATE
              </span>
              <Icon
                className={styles.sortIcon}
                name={getSortIcon('due_date')}
                color={
                  sortConfig.key === 'due_date' ? 'primary' : 'close-darkbg'
                }
              />
            </div>
            <div
              className={cx(styles.columnTitle, styles.sortableHeader)}
              onClick={() => handleSort('name')}
            >
              <span
                className={cx(styles.headerText, {
                  [styles.selected]: sortConfig.key === 'name',
                })}
              >
                TITLE
              </span>
              <Icon
                className={styles.sortIcon}
                name={getSortIcon('name')}
                color={sortConfig.key === 'name' ? 'primary' : 'close-darkbg'}
              />
            </div>
            <div className={styles.columnType}>TYPE</div>
            <div className={styles.columnLease}>LEASE</div>
            <div className={styles.columnStatus}>STATUS</div>
            <div className={styles.columnAction}>ACTION</div>
          </div>
          {sortedReports?.map((report) => {
            const assignedTo = report.assigned_to
              ?.map((assignee) =>
                assignee.user_role === 'OWNER'
                  ? 'You'
                  : `${assignee.first_name} ${assignee.last_name}`,
              )
              ?.join(', ');

            const onClickLeaseTitle = (e) => {
              e.stopPropagation();
              goToLeasePage(report.lease_id);
            };

            const isSelfAssigned =
              report.assigned_to?.[0]?.user_role === 'OWNER';

            return (
              <div
                key={report.id}
                onClick={() =>
                  onRowClick(
                    report.lease_id,
                    report.id,
                    report.status,
                    isSelfAssigned,
                  )
                }
                className={styles.tableRow}
              >
                <div className={styles.columnDueDate}>
                  {report.due_date
                    ? moment(report.due_date).format('MMM D, YYYY')
                    : 'N/A'}
                </div>
                <div className={cx(styles.columnTitle, styles.columnLease)}>
                  <div className={styles.title}>{report.name}</div>
                  {assignedTo && (
                    <div className={styles.assignedTo}>
                      Assigned to {assignedTo}
                    </div>
                  )}
                </div>
                <div className={styles.columnType}>
                  {reportTypeLabels[report.type]}
                </div>
                <div className={cx(styles.columnLease, styles.leaseTitle)}>
                  <span
                    onClick={onClickLeaseTitle}
                    className={styles.clickableLeaseTitleText}
                  >
                    {report.lease?.title}
                  </span>
                </div>
                <div className={styles.columnStatus}>
                  <ConditionReportStatusTag
                    status={report.status ?? CONDITION_REPORT_STATUS.UPLOADED}
                  />
                </div>
                <div className={styles.columnAction}>
                  <ReportAction report={report} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

ConditionReportsTableDesktop.propTypes = {
  className: PropTypes.string,
  conditionReports: PropTypes.arrayOf(PropTypes.object),
  goToLeasePage: PropTypes.func,
  onRowClick: PropTypes.func,
  createNewReportHandler: PropTypes.func,
  hideTitle: PropTypes.bool,
};

export default ConditionReportsTableDesktop;
